// Display mirror of backend/src/lib/dpiaCriteria.ts — lib/__tests__/dpiaCriteria.test.ts runs both over the same
// inputs. The server decides and enforces; this previews the screening while the form is filled in.
//
// WP248: "in most cases" processing meeting two criteria requires a DPIA, and in some cases one is
// enough. So two or more is `likely`, one is `consider` (the controller decides and records why), and
// an Art. 35(3) case is `mandatory` whatever else is true. A `likely` or `consider` activity may be
// recorded as not needing one, with the reason (WP248 asks for it); a mandatory one may not.
//
// Four criteria are read from the record itself (sensitive or highly personal data, large scale, vulnerable
// people, a high-risk AI system — any AI system where UK law applies), and so is Art. 35(3)(b). The rest are the
// member's answers: nothing the platform holds says whether processing profiles people or matches datasets.
//
// Where UK law applies, the ICO's list under Art. 35(4) ("Examples of processing likely to result in high risk")
// makes a DPIA required for its ten kinds of processing; three of them (innovative technology, biometric data,
// genetic data) only when combined with any one WP248 criterion. Those are mandatory cases here, beside Art. 35(3).

import { isSensitiveCategory } from "./dataCategories";

export const WP248_CRITERIA = [
  { id: "evaluation_scoring",    n: 1, label: "Evaluation or scoring",                   help: "Profiling or predicting people's performance at work, economic situation, health, preferences, reliability, behaviour, location or movements." },
  { id: "automated_decisions",   n: 2, label: "Automated decisions with legal or similar effect", help: "Decisions taken by the processing that produce legal effects on people or similarly significantly affect them." },
  { id: "systematic_monitoring", n: 3, label: "Systematic monitoring",                   help: "Observing, monitoring or controlling people, including through networks or in a publicly accessible area." },
  { id: "sensitive_data",        n: 4, label: "Sensitive or highly personal data",       help: "Special category or criminal offence data, or data people regard as highly personal: financial, location, communications, personal documents." },
  { id: "large_scale",           n: 5, label: "Large-scale processing",                  help: "Judged by the number of people, the volume and range of data, how long the processing runs and how far it reaches." },
  { id: "matching_combining",    n: 6, label: "Matching or combining datasets",          help: "Combining data from different purposes or controllers in a way people would not reasonably expect." },
  { id: "vulnerable_subjects",   n: 7, label: "Vulnerable people",                       help: "Children, employees, patients, the elderly, asylum seekers, or anyone in an imbalanced relationship with the organisation." },
  { id: "innovative_technology", n: 8, label: "Innovative use or new technology",        help: "New technological or organisational solutions whose consequences for people may not be known." },
  { id: "prevents_rights",       n: 9, label: "Prevents a right, service or contract",   help: "The processing itself decides whether people can use a service, enter a contract or exercise a right." },
] as const;
export type Wp248CriterionId = (typeof WP248_CRITERIA)[number]["id"];
export const WP248_CRITERION_IDS: readonly string[] = WP248_CRITERIA.map((c) => c.id);

export const ART35_3_CASES = [
  { id: "a", label: "Art. 35(3)(a) Systematic and extensive automated evaluation", help: "Evaluation of personal aspects based on automated processing, including profiling, on which decisions with legal or similarly significant effects are based." },
  { id: "b", label: "Art. 35(3)(b) Large-scale special category or criminal data",  help: "Processing on a large scale of special category data (Art. 9) or criminal conviction and offence data (Art. 10)." },
  { id: "c", label: "Art. 35(3)(c) Large-scale monitoring of a public area",         help: "Systematic monitoring of a publicly accessible area on a large scale." },
] as const;
export type Art35CaseId = (typeof ART35_3_CASES)[number]["id"];
export const ART35_3_CASE_IDS: readonly string[] = ART35_3_CASES.map((c) => c.id);

/** Subject categories WP248 criterion 7 names as vulnerable (employees included: the power imbalance). */
export const VULNERABLE_SUBJECT_CATEGORIES: ReadonlySet<string> = new Set([
  "children", "minors", "pupils", "patients", "vulnerable_adults", "employees", "job_applicants",
]);

/** Words in a free-text subject category that name a vulnerable group ("School children", "Staff", "Care home residents"). */
const VULNERABLE_WORDS = /\b(child|children|minors?|pupils?|students?|young people|patients?|vulnerable|elderly|older people|care home|asylum|refugees?|employees?|staff|workers?|job applicants?|applicants?|candidates?)\b/i;

/** Whether a subject category, as recorded (an id or free text), names a group WP248 criterion 7 counts as vulnerable. */
export function isVulnerableSubject(category: string): boolean {
  const s = category.trim();
  if (!s) return false;
  const id = s.toLowerCase().replace(/[\s-]+/g, "_");
  return VULNERABLE_SUBJECT_CATEGORIES.has(id) || VULNERABLE_WORDS.test(s.replace(/_/g, " "));
}

/** Data people regard as highly personal (WP248 criterion 4): financial and location data, beside Art. 9 / 10 data. */
export const HIGHLY_PERSONAL_CATEGORIES: ReadonlySet<string> = new Set(["financial", "location"]);

/**
 * The ICO's Art. 35(4) list, in our words. `withCriterion`: a DPIA is required only when the item is combined with
 * any WP248 criterion. Six are read from the record where it can; the rest are the member's answers.
 */
export const ICO_HIGH_RISK_LIST = [
  { id: "innovative_technology", label: "Innovative technology (including AI)",          withCriterion: true,  help: "New technology, or a novel use of existing technology, including any AI." },
  { id: "denial_of_service",     label: "Decisions on access to a product, service or benefit", withCriterion: false, help: "Access decided to any extent by automated decision-making or profiling, or involving special category data." },
  { id: "large_scale_profiling", label: "Large-scale profiling",                         withCriterion: false, help: "Any profiling of individuals on a large scale." },
  { id: "biometric",             label: "Biometric data to identify a person",           withCriterion: true,  help: "Processing biometric data to identify someone uniquely." },
  { id: "genetic",               label: "Genetic data",                                  withCriterion: true,  help: "Any processing of genetic data, other than by a health professional for direct care." },
  { id: "data_matching",         label: "Data matching",                                 withCriterion: false, help: "Combining, comparing or matching personal data from several sources." },
  { id: "invisible_processing",  label: "Invisible processing",                          withCriterion: false, help: "Data not obtained from the person where telling them would be impossible or take disproportionate effort." },
  { id: "tracking",              label: "Tracking",                                      withCriterion: false, help: "Tracking a person's geolocation or behaviour, online or offline." },
  { id: "targeting_vulnerable",  label: "Targeting children or other vulnerable people", withCriterion: false, help: "Their data used for marketing, profiling or automated decisions, or online services offered directly to children." },
  { id: "physical_harm",         label: "Risk of physical harm",                         withCriterion: false, help: "A breach of the data could put people's physical health or safety at risk." },
] as const;
export const ICO_HIGH_RISK_IDS: readonly string[] = ICO_HIGH_RISK_LIST.map((i) => i.id);

export interface DpiaScreeningInput {
  dataCategories: readonly string[];
  subjectCategories: readonly string[];
  largeScale: boolean;
  /** Risk classes of the AI systems linked to the activity (lib/aiSystems.ts). */
  aiRiskClasses?: readonly string[];
  /** The criteria the member said apply (any of the nine). */
  declaredCriteria?: readonly string[];
  /** The Art. 35(3) cases the member said apply (a, b or c). */
  declaredCases?: readonly string[];
  /**
   * A UK data protection law is in scope. The ICO's Art. 35(4) list applies (its items become mandatory
   * cases) and counts the use of any AI as innovative technology (criterion 8), not only a high-risk system.
   */
  ukLaw?: boolean;
  /** The ICO list items the member said apply (read only while `ukLaw`). */
  declaredUkTypes?: readonly string[];
  /**
   * The activity monitors workers (declared on the record). The ICO's monitoring workers guidance:
   * systematic monitoring (criterion 3) of people in an imbalanced relationship (criterion 7).
   */
  monitorsWorkers?: boolean;
  /** Art. 9 / Art. 10 categories data discovery found at the activity's locations (backend mirror). */
  discoveredCategories?: readonly string[];
}

export interface DpiaCriterionResult {
  id: string;
  n: number;
  label: string;
  met: boolean;
  /** Read from the record (and not open to un-ticking), or the member's answer. */
  derived: boolean;
  /** Why a derived criterion is met. */
  reason: string | null;
}

export type DpiaLevel = "mandatory" | "likely" | "consider" | "none";

export interface DpiaScreening {
  level: DpiaLevel;
  criteria: DpiaCriterionResult[];
  /** The Art. 35(3) cases that apply. */
  cases: Array<{ id: string; label: string; derived: boolean }>;
  /** One line per case and criterion met, for item descriptions and prompts. */
  reasons: string[];
  metCount: number;
}

const human = (xs: readonly string[]) => xs.map((x) => x.replace(/_/g, " ")).join(", ");

export function dpiaScreening(e: DpiaScreeningInput): DpiaScreening {
  const declared = new Set((e.declaredCriteria ?? []).filter((c) => WP248_CRITERION_IDS.includes(c)));
  const recorded = e.dataCategories.filter(isSensitiveCategory);
  const found = (e.discoveredCategories ?? []).filter((c) => isSensitiveCategory(c) && !recorded.includes(c));
  const sensitive = [...recorded, ...found];
  const personal = e.dataCategories.filter((c) => HIGHLY_PERSONAL_CATEGORIES.has(c));
  const vulnerable = e.subjectCategories.filter(isVulnerableSubject);
  const ai = e.aiRiskClasses ?? [];
  const highRiskAi = ai.some((c) => c === "high" || c === "prohibited");
  const anyAiUk = !!e.ukLaw && ai.length > 0;

  const derivedReason: Partial<Record<Wp248CriterionId, string>> = {
    ...(sensitive.length || personal.length ? { sensitive_data: [
      recorded.length || personal.length ? `${human([...recorded, ...personal])} data` : null,
      found.length ? `${human(found)} data found by data discovery` : null,
    ].filter(Boolean).join("; ") } : {}),
    ...(e.largeScale ? { large_scale: "recorded as large-scale" } : {}),
    ...(vulnerable.length ? { vulnerable_subjects: `data about ${human(vulnerable)}` } : {}),
    ...(highRiskAi ? { innovative_technology: "a high-risk AI system" }
      : anyAiUk ? { innovative_technology: "an AI system (ICO Art. 35(4) list: innovative technology, including AI)" } : {}),
    ...(e.monitorsWorkers ? { systematic_monitoring: "monitors workers (ICO monitoring workers guidance)" } : {}),
    ...(e.monitorsWorkers && !vulnerable.length ? { vulnerable_subjects: "workers, in an imbalanced relationship with the organisation" } : {}),
  };

  const criteria: DpiaCriterionResult[] = WP248_CRITERIA.map((c) => {
    const reason = derivedReason[c.id] ?? null;
    return { id: c.id, n: c.n, label: c.label, met: !!reason || declared.has(c.id), derived: !!reason, reason };
  });

  const declaredCases = new Set((e.declaredCases ?? []).filter((c) => ART35_3_CASE_IDS.includes(c)));
  const bDerived = sensitive.length > 0 && e.largeScale;
  const cases: Array<{ id: string; label: string; derived: boolean }> = ART35_3_CASES
    .filter((c) => declaredCases.has(c.id) || (c.id === "b" && bDerived))
    .map((c) => ({ id: c.id, label: c.label, derived: c.id === "b" && bDerived }));

  const met = criteria.filter((c) => c.met);
  if (e.ukLaw) {
    const has = (id: string) => criteria.some((c) => c.id === id && c.met);
    const declaredUk = new Set((e.declaredUkTypes ?? []).filter((id) => ICO_HIGH_RISK_IDS.includes(id)));
    const derivedUk: Record<string, boolean> = {
      innovative_technology: ai.length > 0,
      denial_of_service: has("prevents_rights") && (has("automated_decisions") || sensitive.length > 0),
      large_scale_profiling: has("evaluation_scoring") && e.largeScale,
      biometric: e.dataCategories.includes("biometric"),
      genetic: e.dataCategories.includes("genetic"),
      data_matching: has("matching_combining"),
    };
    for (const item of ICO_HIGH_RISK_LIST) {
      const derived = !!derivedUk[item.id];
      if (!derived && !declaredUk.has(item.id)) continue;
      // "Combined with any other criterion": the criterion the item itself sets does not count twice.
      const own = item.id === "innovative_technology" ? "innovative_technology" : item.id === "biometric" || item.id === "genetic" ? "sensitive_data" : null;
      if (item.withCriterion && !met.some((c) => c.id !== own)) continue;
      cases.push({ id: `ico_${item.id}`, label: `ICO Art. 35(4) list: ${item.label.toLowerCase()}`, derived });
    }
  }
  const reasons = [
    ...cases.map((c) => c.label),
    ...met.map((c) => `criterion ${c.n}, ${c.label.toLowerCase()}${c.reason ? ` (${c.reason})` : ""}`),
  ];
  const level: DpiaLevel = cases.length ? "mandatory" : met.length >= 2 ? "likely" : met.length === 1 ? "consider" : "none";
  return { level, criteria, cases, reasons, metCount: met.length };
}

/** Shortest reason a member must give for recording that a `likely` or `consider` activity needs no DPIA. */
export const NOT_REQUIRED_REASON_MIN = 20;

/** Why the "no DPIA needed" reason is refused, or null. A mandatory case can never be reasoned away. */
export function notRequiredReasonError(level: DpiaLevel, reason: string | null | undefined): string | null {
  if (!reason?.trim()) return null;
  if (level === "mandatory") return "A DPIA is mandatory for this processing (GDPR Art. 35(3)), so it cannot be recorded as not needed.";
  if (reason.trim().length < NOT_REQUIRED_REASON_MIN) return `Say why this processing is not likely to result in a high risk (at least ${NOT_REQUIRED_REASON_MIN} characters).`;
  return null;
}

// ── The criteria on a DPIA itself ───────────────────────────────────────────

/** A DPIA's own record against each criterion: whether it applies and why. */
export type DpiaCriteriaRecord = Record<string, { met: boolean; note?: string }>;

/** Keeps only the nine ids and a bounded note; anything else sent is dropped. */
export function sanitiseCriteriaRecord(raw: unknown): DpiaCriteriaRecord {
  const out: DpiaCriteriaRecord = {};
  if (!raw || typeof raw !== "object") return out;
  for (const id of WP248_CRITERION_IDS) {
    const v = (raw as Record<string, unknown>)[id];
    if (!v || typeof v !== "object") continue;
    const met = (v as { met?: unknown }).met === true;
    const note = typeof (v as { note?: unknown }).note === "string" ? ((v as { note: string }).note).trim().slice(0, 2000) : "";
    out[id] = note ? { met, note } : { met };
  }
  return out;
}

/** A new DPIA's starting record from the processing activity it assesses. */
export function criteriaRecordFromScreening(s: DpiaScreening): DpiaCriteriaRecord {
  const out: DpiaCriteriaRecord = {};
  for (const c of s.criteria) if (c.met) out[c.id] = c.reason ? { met: true, note: `From the processing record: ${c.reason}.` } : { met: true };
  return out;
}

/** Criteria the DPIA has not answered, met or not. */
export const unansweredCriteria = (r: DpiaCriteriaRecord): string[] => WP248_CRITERION_IDS.filter((id) => !r[id]);
